import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@lib/shared/services/toast.service';
import { OpeningMessageService } from '@lib/shared/services/opening-message.service';
import { CredentialsService } from 'lib/core';
import { RegisterService } from 'lib/core/register.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { environment } from '@lib/environments/environment';
import { getSignUpErrorMessage } from '../../auth-error-messages';
import * as Sentry from '@sentry/angular';
import { AuthenticationService } from '@lib/core';

@Component({
  selector: 'app-create-licence',
  templateUrl: './create-licence.component.html',
  styleUrls: ['./create-licence.component.scss'],
  standalone: false,
})
@Sentry.TraceClass({ name: 'CreateLicenceComponent' })
export class CreateLicenceComponent {
  public form: UntypedFormGroup;
  public submitted = false;
  public isSubmitting = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private registerService: RegisterService,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
    private toastService: ToastService,
    private openingMessageService: OpeningMessageService,
    private fns: AngularFireFunctions,
  ) {
    this.createForm();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      company: ['', Validators.required],
      address1: ['', Validators.required],
      address2: [''],
      address3: [''],
      townCity: ['', Validators.required],
      postcode: ['', Validators.required],
      phone: ['', Validators.required],
      howHeard: ['', Validators.required],
      jobTitle: ['', Validators.required],
      howHeardOther: [''],
    });
    this.addValidator();
  }

  addValidator(): void {
    const howHeardOther = this.form.get('howHeardOther');
    this.form.get('howHeard').valueChanges.subscribe({
      next: (value) => {
        howHeardOther.setValidators(value === 'Other' ? [Validators.required] : null);
        howHeardOther.updateValueAndValidity();
      },
      error: (error) => {
        console.error(error);
        this.toastService.showDissmissableErrorToast('Error getting howHeard value.');
      },
    });
  }

  async onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    this.submitted = true;
    if (this.form.valid) {
      this.isSubmitting = true;
      this.registerService.licence$.next(this.form.value);
      this.registerService.signUpMethod$.next('signUpForm');
      this.authenticationService.nextLoading(true);
      try {
        const account = await this.registerService.createAccount();
        if (account?.licenceId) {
          localStorage.setItem('licenceId', account.licenceId);
        }
      } catch (error) {
        console.error(error);
        const code = (error as { code?: string })?.code;
        if (code === 'auth/email-already-in-use') {
          const appName = environment.appType === 'outdoor' ? 'outdoorPro' : 'pePro';
          this.fns.httpsCallable('notifyDuplicateSignUp')({
            email: this.registerService.user$.value.email,
            appName,
          }).subscribe();
        }
        this.toastService.showDissmissableErrorToast(getSignUpErrorMessage(code));
        this.isSubmitting = false;
        this.authenticationService.nextLoading(false);
        return;
      }
      try {
        const profileLoaded = await this.credentialsService.getUserData();
        if (!profileLoaded) {
          await this.credentialsService.getUserData({ retryMissingProfile: true });
        }
        this.openingMessageService.setPendingForSignup();
        await this.router.navigate(['/tabs/home']);
      } finally {
        this.isSubmitting = false;
        this.authenticationService.nextLoading(false);
      }
    }
  }
}
